import clonedeep from 'lodash.clonedeep';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { compose } from 'lodash/fp';
import React, { Component, Fragment } from 'react';
import {
  calculateTotalPricePerCurrency,
  findCurrencyItemFromListWithFallback,
  getInitialCurrencyState,
} from '../../../util/currency';
import { errorCodes, getErrorMessageKey } from '../../../util/errorCodes';
import jsonPost from '../../../util/jsonPost';
import Button from '../Atoms/Button';
import {
  accomTypes,
  getOptionUpgrade,
  getTotalPrice,
  roomTypeCodes,
  tourBookingStep,
} from '../BookingFieldset/helpers';
import Excursions from '../BookingUpgrades/Excursions';
import HotelGroup from '../BookingUpgrades/HotelGroup';
import { labelsPropTypes } from '../BookingUpgrades/PropTypes';
import RoomSelection from '../BookingUpgrades/RoomSelection';
import UpgradeDropdown from '../BookingUpgrades/UpgradeDropdown';
import {
  buildCapacityErrorMessage,
  buildStopoverItineraryDescriptions,
  getSelectedOptionWithGroupKey,
  injectItineraryDescriptions,
  isAtolProtected,
  mergeDefaultAndUpgrades,
} from '../BookingUpgrades/helpers';
import { withErrorBoundary } from '../Core/ErrorBoundary';
import ErrorMessage from '../Core/ErrorMessage';
import ItinerarySidebar from '../ItinerarySidebar';
import PackageSummary from '../PackageSummary';
import StickyComponent from '../StickyComponent';
import { stopoverOptionsPackagePropTypes } from './PropTypes';
import {
  StopoverTypes,
  createSelectedStopover,
  getCheapestStopoverData,
  getMutableSelectedStopover,
  getStopoverDataWithDataKey,
  getStopoverOptionWithSelectedStopover,
  getTourDatesWithStopoversConsidered,
} from './helpers';
import CUSTOM_EVENT from '../../../util/customEvents';
import withMapReversePortal from '../../../hocs/withMapReversePortal';
import { portalNodeType } from '../../../util/sharedPropTypes';
import GoogleMapComponent from '../GoogleMapComponent';

const propTypes = exact({
  apiURL: PropTypes.string.isRequired,
  labels: labelsPropTypes,
  package: stopoverOptionsPackagePropTypes.isRequired,
  selectedStep: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
  // Below is props from withReversePortal HOC
  portalNode: portalNodeType,
});

const defaultProps = {
  labels: undefined,
  portalNode: null,
};

class BookingStopoverOptions extends Component {
  constructor(props) {
    super(props);
    this.handleStopoverExcursionSelect = this.handleStopoverExcursionSelect.bind(this);
    this.handleStopoverHotelSelect = this.handleStopoverHotelSelect.bind(this);
    this.handleRoomChange = this.handleRoomChange.bind(this);
    this.handleItineraryToggleClick = this.handleItineraryToggleClick.bind(this);
    this.handleNumberOfNightsChange = this.handleNumberOfNightsChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleResetState = this.handleResetState.bind(this);
    this.updateAllStopoverHotelRooms = this.updateAllStopoverHotelRooms.bind(this);
    this.handleCurrencyChange = this.handleCurrencyChange.bind(this);

    const currentCurrency = getInitialCurrencyState(
      props.package?.pricePerPax[0]?.pricePerCurrency
    );

    const totalPricePerCurrency = calculateTotalPricePerCurrency(props.package);
    const currencyPriceSetting = findCurrencyItemFromListWithFallback(
      totalPricePerCurrency,
      currentCurrency.code
    );

    const state = {
      ...props.package?.selectedOptions,
      activeModalId: null,
      callToBook: false,
      errorMessage: null,
      isLoading: false,
      itinerary: props.package?.itinerarySideBar?.itinerary,
      itineraryUpdated: false,
      currentCurrencyCodeAndSymbol: currentCurrency,
      currencyPriceSetting,
    };
    state.totalPrice = getTotalPrice(state, props.package, currencyPriceSetting);
    this.state = state;
  }

  componentDidMount() {
    window.addEventListener('pageshow', this.handleResetState);
    window.addEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
  }

  componentWillUnmount() {
    window.removeEventListener('pageshow', this.handleResetState);
    window.removeEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
  }

  handleCurrencyChange(event) {
    const newCurrencyCode = event?.detail?.code;
    if (newCurrencyCode) {
      this.setState({ currentCurrencyCodeAndSymbol: event.detail });
    } else console.error('Currency data not found for:', newCurrencyCode);
    const newCurrencyPriceSetting = findCurrencyItemFromListWithFallback(
      this.props.package.pricePerCurrency,
      newCurrencyCode
    );

    if (newCurrencyPriceSetting)
      this.setState({
        currencyPriceSetting: newCurrencyPriceSetting,
        totalPrice: getTotalPrice(this.state, this.props.package, newCurrencyPriceSetting),
      });
    else console.error('Currency not found from current currency chosen to currency from model');
  }

  handleResetState() {
    this.setState({ isLoading: false });
  }

  updateAllStopoverHotelRooms() {
    const mutableState = clonedeep(this.state);

    let rooms = [];
    if (
      mutableState.accommodations.filter((a) => a.accommodationType === accomTypes.HOTEL).length > 0
    ) {
      rooms = mutableState.accommodations.filter((a) => a.accommodationType === accomTypes.HOTEL)[0]
        .rooms;
    } else {
      const matchingStopover = getStopoverOptionWithSelectedStopover(
        this.props.package?.stopoverOptions,
        mutableState.stopovers[0]
      );

      if (matchingStopover) {
        const { defaultOption = {}, upgrades = [] } = matchingStopover.option.hotelOption || {};
        const allHotelOptions = [defaultOption, ...upgrades];
        const selectedHotel = allHotelOptions.find(
          (hotelOption) => hotelOption.option.key === this.state.stopovers[0].hotel.optionKey
        );

        rooms = selectedHotel.roomTypes.map((roomType) => ({
          roomKey: roomType.key,
          roomCount: 0,
        }));
      }
    }

    mutableState.stopovers = mutableState.stopovers.map((stopover) => ({
      ...stopover,
      hotel: {
        ...stopover.hotel,
        rooms,
      },
    }));

    this.setState(mutableState);
  }

  handleNumberOfNightsChange(dataKey, stopoverGroupKey) {
    const mutableState = clonedeep(this.state);

    const selectedStopover = mutableState.stopovers.find((s) => s?.groupKey === stopoverGroupKey);
    selectedStopover.dataKey = dataKey;

    const flightOptions = getStopoverDataWithDataKey(
      this.props.package?.stopoverOptions,
      dataKey
    )?.flightOptions;

    selectedStopover.flights =
      flightOptions && flightOptions.length
        ? flightOptions.map((flightOption) => ({
            flightClassKey: flightOption.defaultOption.flightClasses.find((x) => !x.soldOut).key,
            groupKey: flightOption.key,
            optionKey: flightOption.defaultOption.key,
          }))
        : null;

    mutableState.totalPrice = getTotalPrice(
      mutableState,
      this.props.package,
      this.state.currencyPriceSetting
    );
    mutableState.itineraryUpdated = true;

    this.setState(mutableState);
  }

  handleStopoverHotelSelect(stopoverGroupKey, stopoverOptionKey, hotelGroupKey, hotelOptionKey) {
    const mutableState = clonedeep(this.state);
    const selectedStopover = getMutableSelectedStopover(mutableState, stopoverGroupKey);
    const { data } = getOptionUpgrade(
      stopoverGroupKey,
      stopoverOptionKey,
      this.props.package?.stopoverOptions
    );

    if (selectedStopover) {
      selectedStopover.hotel = {
        groupKey: hotelGroupKey,
        optionKey: hotelOptionKey,
      };
    } else {
      const { dataKey: existingDataKey } = mutableState.stopovers.find(
        ({ groupKey }) => groupKey === stopoverGroupKey
      );
      const dataKey =
        existingDataKey ||
        getCheapestStopoverData(data, this.state.currencyPriceSetting)?.key ||
        '';
      createSelectedStopover(mutableState, {
        groupKey: stopoverGroupKey,
        optionKey: stopoverOptionKey,
        dataKey,
        excursion: null,
        hotel: {
          accommodationType: accomTypes.HOTEL,
          groupKey: hotelGroupKey,
          optionKey: hotelOptionKey,
        },
      });
    }

    mutableState.totalPrice = getTotalPrice(
      mutableState,
      this.props.package,
      this.state.currencyPriceSetting
    );
    mutableState.itineraryUpdated = true;
    this.setState(mutableState, () => {
      this.updateAllStopoverHotelRooms();
    });
  }

  handleStopoverExcursionSelect(
    stopoverGroupKey,
    stopoverOptionKey,
    excursionGroupKey,
    excursionOptionKey
  ) {
    const mutableState = clonedeep(this.state);
    const selectedStopover = getMutableSelectedStopover(mutableState, stopoverGroupKey);
    const { data } = getOptionUpgrade(
      stopoverGroupKey,
      stopoverOptionKey,
      this.props.package?.stopoverOptions
    );
    const excursionAlreadySelected = selectedStopover?.excursion?.optionKey === excursionOptionKey;

    if (excursionAlreadySelected) {
      selectedStopover.excursion = null;
    } else if (selectedStopover) {
      selectedStopover.excursion = {
        groupKey: excursionGroupKey,
        optionKey: excursionOptionKey,
      };
    } else {
      const { dataKey: existingDataKey } = mutableState.stopovers.find(
        ({ groupKey }) => groupKey === stopoverGroupKey
      );
      const dataKey =
        existingDataKey ||
        getCheapestStopoverData(data, this.state.currencyPriceSetting)?.key ||
        '';

      createSelectedStopover(mutableState, {
        groupKey: stopoverGroupKey,
        optionKey: stopoverOptionKey,
        dataKey,
        excursion: {
          groupKey: excursionGroupKey,
          optionKey: excursionOptionKey,
        },
        hotel: null,
      });
    }

    mutableState.totalPrice = getTotalPrice(
      mutableState,
      this.props.package,
      this.state.currencyPriceSetting
    );
    mutableState.itineraryUpdated = true;
    this.setState(mutableState);
  }

  /**
   * @param {number} roomCount
   * @param {string} roomKey
   */
  handleRoomChange(roomCount, roomKey) {
    const mutableState = clonedeep(this.state);

    // Selected room state may be null within stopover hotel state,
    // to access selected room state we use the first stopover's hotel,
    // as all room state is replicated across all stopover hotels.
    const rooms = mutableState.stopovers[0].hotel.rooms
      ? mutableState.stopovers[0].hotel.rooms
      : [];

    const room = rooms?.find((r) => r.roomKey === roomKey);
    if (room) {
      if (room.roomCount === roomCount) return;
      room.roomCount = roomCount;
    } else {
      rooms.push({ roomCount, roomKey });
    }

    // the selected room state is mirrored across all selected stopover hotels,
    // as the same room config once set is the same for all.
    const updatedStopovers = mutableState.stopovers.map((stopover) => ({
      ...stopover,
      hotel: {
        ...stopover.hotel,
        rooms,
      },
    }));

    mutableState.stopovers = updatedStopovers;
    mutableState.totalPrice = getTotalPrice(
      mutableState,
      this.props.package,
      this.state.currencyPriceSetting
    );
    mutableState.itineraryUpdated = true;
    this.setState(mutableState);
  }

  handleItineraryToggleClick() {
    this.setState({ itineraryUpdated: false });
  }

  handleSubmit() {
    const {
      stopovers: stateStopovers,
      partySize,
      accommodations,
      currentCurrencyCodeAndSymbol,
    } = this.state;
    // Reset form errors and initialise loading status
    this.setState({
      errorMessage: null,
      isLoading: true,
    });

    let stopovers = stateStopovers;

    if (accommodations.filter((a) => a.accommodationType === accomTypes.HOTEL).length > 0) {
      stopovers = stateStopovers.map((stopover) => ({
        ...stopover,
        hotel: {
          ...stopover.hotel,
          rooms: accommodations.filter((a) => a.accommodationType === accomTypes.HOTEL)[0].rooms,
        },
      }));
    }

    // Validate party size matches the total rooms capacity
    if (stopovers[0].hotel.rooms.length > 0) {
      const { optionKey, rooms } = stopovers[0].hotel || {};

      const matchingStopover = getStopoverOptionWithSelectedStopover(
        this.props.package?.stopoverOptions,
        stopovers[0]
      );

      if (matchingStopover) {
        const capacity = rooms.reduce((accumulator, room) => {
          const { defaultOption = {}, upgrades = [] } = matchingStopover.option.hotelOption || {};
          const allHotelOptions = [defaultOption, ...upgrades];
          const { roomTypes = [] } = allHotelOptions.find(({ key }) => key === optionKey) || {};
          const { code } = roomTypes.find((r) => r.key === room.roomKey) || {};
          const roomCapacity = code === roomTypeCodes.SINGLE_ROOM ? 1 : 2;
          return accumulator + room.roomCount * roomCapacity;
        }, 0);

        const errorMessage = buildCapacityErrorMessage(
          capacity,
          partySize,
          this.props.package?.enforceAllRoomsFilledValidation,
          this.props.labels
        );
        if (errorMessage) return this.setState({ errorMessage, isLoading: false });
      }
    }

    // Post form data to API
    const options = {
      stopovers,
      orderId: this.props.orderId,
      currency: currentCurrencyCodeAndSymbol.code,
    };

    return jsonPost(this.props.apiURL, options, (response) => {
      if (response.errorCode || response.errors) {
        if (response.errorCode === errorCodes.TourBookingTourHasReachedPaxCapLimit) {
          window.location.href = response.redirectUri;
        }
        const errorMessageKey = getErrorMessageKey(response.errorCode);
        return this.setState({
          errorMessage:
            this.props.labels[errorMessageKey] || response.message || 'An error occurred',
          isLoading: false,
        });
      }
      window.location.href = response.redirectUri;
      return this.setState({ errorMessage: null });
    });
  }

  render() {
    const itinerarySideBar = {
      ...this.props.package?.itinerarySideBar,
      isCallToBook: this.state.callToBook,
    };
    const { callToBookButtonText } = this.props.package?.itinerarySideBar || {};
    const { labels } = this.props;

    const showAtolLabel = isAtolProtected(this.state.flights);

    const { updatedTourFromDate, updatedTourToDate } = getTourDatesWithStopoversConsidered(
      this.props.package,
      this.state.stopovers
    );

    let availableRooms = [];
    let selectedRoomTypes = [];

    if (
      this.state.accommodations.filter((a) => a.accommodationType === accomTypes.HOTEL)?.length ===
      0
    ) {
      const { optionKey, rooms } = this.state.stopovers[0]?.hotel || {};

      const matchingStopover = getStopoverOptionWithSelectedStopover(
        this.props.package?.stopoverOptions,
        this.state.stopovers[0]
      );

      if (matchingStopover) {
        const { defaultOption = {}, upgrades = [] } = matchingStopover.option.hotelOption || {};
        const allHotelOptions = [defaultOption, ...upgrades];
        const { roomTypes = [] } = allHotelOptions.find(({ key }) => key === optionKey) || {};

        selectedRoomTypes = roomTypes || [];
        availableRooms =
          rooms ||
          roomTypes.map((roomType) => ({
            roomKey: roomType.key,
            roomCount: 0,
          }));
      }
    }

    return (
      <section className="Booking">
        <form noValidate autoComplete="false" onSubmit={this.handleSubmit}>
          {/* Itinerary */}
          <ItinerarySidebar
            {...itinerarySideBar}
            fromDate={updatedTourFromDate}
            toDate={updatedTourToDate}
            handleToggleClick={this.handleItineraryToggleClick}
            hideBookButton
            isUpdated={this.state.itineraryUpdated}
            itinerary={injectItineraryDescriptions(
              this.state,
              this.props.package,
              this.props.selectedStep
            )}
            perPersonText={labels.perPersonText}
            priceTotal={this.state.totalPrice}
            preEventStopoverItinerary={buildStopoverItineraryDescriptions(
              this.props.package.stopoverOptions,
              this.state.stopovers,
              StopoverTypes.PRE_EVENT,
              updatedTourFromDate
            )}
            postEventStopoverItinerary={buildStopoverItineraryDescriptions(
              this.props.package.stopoverOptions,
              this.state.stopovers,
              StopoverTypes.POST_EVENT
            )}
          />
          {/* Package summary */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              <StickyComponent contained>
                <PackageSummary
                  atolProtectedText={labels.atolProtectedText}
                  callToBookText={callToBookButtonText}
                  durationText={labels.durationText}
                  fromDate={updatedTourFromDate}
                  toDate={updatedTourToDate}
                  description={this.props.package?.tourDescription}
                  isCallToBook={this.state.callToBook}
                  preferredCardImage={labels.preferredCardImage}
                  price={this.state.totalPrice}
                  currencySymbol={this.state.currentCurrencyCodeAndSymbol.symbol}
                  showAtolLabel={showAtolLabel}
                  title={this.props.package?.tourName}
                  totalPriceText={labels.totalPriceText}
                />
              </StickyComponent>
            </div>
          </div>
          {this.props.package.stopoverOptions.map((stopoverGroup) => {
            const selectedStopover = getSelectedOptionWithGroupKey(
              this.state.stopovers,
              stopoverGroup.key
            );

            const selectedOptionKey = selectedStopover?.optionKey || '';
            const selectedStopoverOption = stopoverGroup.options.find(
              ({ key }) => key === selectedOptionKey
            );
            const stopoverIndex = stopoverGroup.options.findIndex(
              ({ key }) => key === selectedOptionKey
            );

            if (stopoverIndex === -1) return null;

            const durationOptions = selectedStopoverOption.data.map(
              ({ key, numberOfNights, soldOut, limitedAvailability, limitedAvailabilityText }) => ({
                disabled: soldOut,
                text: `${numberOfNights}${
                  limitedAvailability && !soldOut ? ` - ${limitedAvailabilityText}` : ''
                }${soldOut ? ` - ${labels.soldOutText}` : ''} `,
                value: key,
              })
            );

            const selectedDurationKey = selectedStopover?.dataKey || '';
            const selectedHotelKey = selectedStopover?.hotel?.optionKey || '';
            const hotelOptionGroup = selectedStopoverOption.option.hotelOption || {};
            const hotelOptions = mergeDefaultAndUpgrades(hotelOptionGroup);
            const selectedExcursionKey = selectedStopover?.excursion?.optionKey || '';
            const excursionOptionGroup = selectedStopoverOption.option.excursionOption || {};
            const isPreStopover = stopoverGroup.stopoverType === StopoverTypes.PRE_EVENT;

            return (
              <Fragment key={stopoverGroup.key}>
                {/* Number Of Nights */}
                <div className="Booking_group">
                  <h2 className="Booking_groupTitle">
                    {isPreStopover
                      ? labels.preEventDurationTitleText
                      : labels.postEventDurationTitleText}
                  </h2>
                  <div className="Booking_content Booking_content-inset">
                    <UpgradeDropdown
                      handleChange={({ target }) =>
                        this.handleNumberOfNightsChange(target.value, stopoverGroup.key)
                      }
                      labelText={labels.durationLabelText}
                      options={durationOptions}
                      selectedValue={selectedDurationKey}
                    />
                  </div>
                </div>

                {/* Stopover Hotels */}
                {hotelOptions.length > 0 && (
                  <div className="Booking_group">
                    <div className="Booking_content">
                      <h2 className="Booking_groupTitle">
                        {isPreStopover
                          ? labels.preEventAccommodationTitleText
                          : labels.postEventAccommodationTitle}
                      </h2>
                      <HotelGroup
                        currencyPriceSetting={this.state.currencyPriceSetting}
                        currencySymbol={this.state.currentCurrencyCodeAndSymbol.symbol}
                        groupKey={hotelOptionGroup.key}
                        handleOptionSelect={(groupKey, optionKey) =>
                          this.handleStopoverHotelSelect(
                            stopoverGroup.key,
                            selectedOptionKey,
                            groupKey,
                            optionKey
                          )
                        }
                        optionAddedText={labels.addedToPackageText}
                        options={hotelOptions}
                        perPersonText={labels.perPersonText}
                        selectedKey={selectedHotelKey}
                        portalNode={this.props.portalNode}
                      />
                    </div>
                  </div>
                )}

                {/* Stopover Excursions */}
                {excursionOptionGroup.options?.length > 0 && (
                  <div className="Booking_group">
                    <div className="Booking_content">
                      <h2 className="Booking_groupTitle">
                        {isPreStopover
                          ? labels.preEventExcursionsTitle
                          : labels.postEventExcursionsTitle}
                      </h2>
                      <Excursions
                        currencyPriceSetting={this.state.currencyPriceSetting}
                        currencySymbol={this.state.currentCurrencyCodeAndSymbol.symbol}
                        dateText={labels.excursionsDateText}
                        groupKey={excursionOptionGroup.key}
                        handleFieldSelect={(groupKey, optionKey) =>
                          this.handleStopoverExcursionSelect(
                            stopoverGroup.key,
                            selectedOptionKey,
                            groupKey,
                            optionKey
                          )
                        }
                        options={excursionOptionGroup.options}
                        perPersonText={labels.perPersonText}
                        selectedKey={selectedExcursionKey}
                        selectedText={labels.addedToPackageText}
                        timeText={labels.excursionsTimeText}
                      />
                    </div>
                  </div>
                )}
              </Fragment>
            );
          })}
          {/* Rooms */}
          {availableRooms.length > 0 && (
            <div className="Booking_group">
              <div className="Booking_content Booking_content-inset">
                <h2 className="Booking_groupTitle">{labels.roomsTitle}</h2>
                <div className="Booking_groupDescription">
                  {labels.singleRoomSupplementText ??
                    'If you require a room for one person, a single room supplement (SRS) will apply.'}
                </div>
                <RoomSelection
                  handleChange={this.handleRoomChange}
                  partySize={this.state.partySize}
                  roomTypes={selectedRoomTypes}
                  selectedValues={availableRooms}
                />
              </div>
            </div>
          )}
          {/* Submit */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              {this.state.errorMessage && (
                <div className="Booking_item">
                  <ErrorMessage text={this.state.errorMessage} />
                </div>
              )}
              <div className="Booking_item Booking_item-center">
                {!this.state.callToBook && (
                  <Button
                    buttonType="button"
                    handleClick={this.handleSubmit}
                    isLoading={this.state.isLoading}
                    modifier="fill"
                    text={labels.submitButtonText}
                  />
                )}
                {this.state.callToBook && (
                  <div className="Booking_callToBook">{callToBookButtonText}</div>
                )}
              </div>
            </div>
          </div>
        </form>
      </section>
    );
  }
}

BookingStopoverOptions.propTypes = propTypes;
BookingStopoverOptions.defaultProps = defaultProps;

// We render Map inside a portal and BookingStopoverOptions is the component we want to wrap.
// This reverse portal allows us to use 1 map instance across a variety of modals on BookingStopoverOptions
// without mount/unmount and memory leaks.
// The HOC will provide portalNode to BookingStopoverOptions, and render Map inside Portals.InPortal.
export default compose(
  withErrorBoundary,
  withMapReversePortal(GoogleMapComponent, {
    skip: (props) => !props.labels.googleApiKey,
  })
)(BookingStopoverOptions);
